import { Lunar } from 'lunar-javascript';

/**
 * 清除日期对象的时分秒，仅保留年月日
 * @param {Date} date - 原始日期
 * @returns {Date} 清除时分秒后的日期
 */
const clearTime = (date) => {
    const newDate = new Date(date);
    newDate.setHours(0, 0, 0, 0);
    return newDate;
};

/**
 * 计算两个日期之间的天数差
 * @param {Date} date1 - 起始日期
 * @param {Date} date2 - 目标日期
 * @returns {number} 天数差（向上取整，当天返回0）
 */
const calcDaysDiff = (date1, date2) => {
    const oneDayMs = 24 * 60 * 60 * 1000; // 一天的毫秒数
    const diffMs = date2 - date1;
    return Math.ceil(diffMs / oneDayMs);
};

/**
 * 判断是否为闰年
 * @param {number} year - 年份
 * @returns {boolean} 是否为闰年
 */
const isLeapYear = (year) => {
    return (year % 4 === 0 && year % 100 !== 0) || (year % 400 === 0);
};

/**
 * 计算阳历下一次生日的日期
 * @param {string} birthday - 阳历生日（格式：YYYY-MM-DD）
 * @param {Date} currentDate - 当前日期（默认：当天）
 * @returns {Date} 下一次阳历生日的日期
 */
const getSolarNextBirthday = (birthday, currentDate = new Date()) => {
    const [birthYear, birthMonth, birthDay] = birthday.split('-').map(Number);
    const current = clearTime(currentDate);
    const currentYear = current.getFullYear();
    let targetYear = currentYear;

    // 构造今年的生日日期（月份是0-based）
    let targetDate = new Date(targetYear, birthMonth - 1, birthDay);

    // 处理2月29日在非闰年的情况
    if (birthMonth === 2 && birthDay === 29 && !isLeapYear(targetYear)) {
        targetDate = new Date(targetYear, 1, 28); // 2月对应0-based的1
    }

    // 若今年生日已过，取明年
    if (clearTime(targetDate) < current) {
        targetYear += 1;
        if (birthMonth === 2 && birthDay === 29) {
            targetDate = isLeapYear(targetYear)
                ? new Date(targetYear, 1, 29)
                : new Date(targetYear, 1, 28);
        } else {
            targetDate = new Date(targetYear, birthMonth - 1, birthDay);
        }
    }

    return clearTime(targetDate);
};

/**
 * 计算阴历下一次生日的日期（转换为阳历）
 * @param {string} birthday - 阴历生日（格式：YYYY-MM-DD，闰月加后缀-1，如：2000-05-05-1）
 * @param {Date} currentDate - 当前日期（默认：当天）
 * @returns {Date} 下一次阴历生日对应的阳历日期
 */
const getLunarNextBirthday = (birthday, currentDate = new Date()) => {
    const parts = birthday.split('-').map(Number);
    const [, lunarMonth, lunarDay] = parts; // 年份不影响，仅取月/日
    const isLeap = parts[3] === 1; // 是否为闰月（第四个参数为1表示闰月）
    const current = clearTime(currentDate);
    const currentYear = current.getFullYear();

    // 转换今年的阴历生日为阳历
    let lunar = Lunar.fromYmd(currentYear, lunarMonth, lunarDay, 0, 0, 0, isLeap);
    let solar = lunar.getSolar();
    let targetDate = clearTime(new Date(solar.getYear(), solar.getMonth() - 1, solar.getDay()));

    // 若今年阴历生日已过，取明年
    if (targetDate < current) {
        lunar = Lunar.fromYmd(currentYear + 1, lunarMonth, lunarDay, 0, 0, 0, isLeap);
        solar = lunar.getSolar();
        targetDate = clearTime(new Date(solar.getYear(), solar.getMonth() - 1, solar.getDay()));
    }

    return targetDate;
};

/**
 * 计算距离下一次生日的天数
 * @param {string} birthday - 生日（阳历：YYYY-MM-DD；阴历：YYYY-MM-DD，闰月：YYYY-MM-DD-1）
 * @param {number} birthdayType - 类型（0=阴历，1=阳历）
 * @param {Date} currentDate - 当前日期（可选，默认当天）
 * @returns {number} 距离下次生日的天数
 */
const getDaysUntilBirthday = (birthday, birthdayType, currentDate = new Date()) => {
    const current = clearTime(currentDate);
    let nextBirthDate;
    if (birthdayType == 1) {
        nextBirthDate = getSolarNextBirthday(birthday, current);
    } else {
        nextBirthDate = getLunarNextBirthday(birthday, current);
    }
    return calcDaysDiff(current, nextBirthDate);
};

export default {
    getDaysUntilBirthday
};